
import React from 'react';
import { RocketIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  
  // Function to handle navigation and scroll to top
  const handleNavigation = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };
  
  return (
    <footer className="bg-feature-gradient border-t border-border">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-1">
            <div className="flex items-center space-x-2 mb-4">
              <RocketIcon className="h-6 w-6 text-primary" />
              <span className="font-bold text-xl">StartupFundraising.com</span>
            </div>
            <div className="flex space-x-4 mb-6">
              <a 
                href="https://x.com/acremades" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-foreground/70 hover:text-primary transition-colors"
                aria-label="Twitter"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a>
              <a 
                href="https://www.linkedin.com/in/acremades/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-foreground/70 hover:text-primary transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-background/50 rounded-lg p-6">
                <h3 className="font-semibold text-lg mb-4">Legal & Support</h3>
                <ul className="grid grid-cols-1 gap-4">
                  <li><button onClick={() => handleNavigation('/privacy-policy')} className="text-left text-foreground/70 hover:text-primary transition-colors">Privacy Policy</button></li>
                  <li><button onClick={() => handleNavigation('/terms-and-conditions')} className="text-left text-foreground/70 hover:text-primary transition-colors">Terms & Conditions</button></li>
                  <li><button onClick={() => handleNavigation('/disclaimer')} className="text-left text-foreground/70 hover:text-primary transition-colors">Disclaimer</button></li>
                  <li><button onClick={() => handleNavigation('/cookie-policy')} className="text-left text-foreground/70 hover:text-primary transition-colors">Cookie Policy</button></li>
                  <li><button onClick={() => handleNavigation('/cancellation-guide')} className="text-left text-foreground/70 hover:text-primary transition-colors">Cancellation Policy</button></li>
                  <li>
                    <button 
                      onClick={() => handleNavigation('/accessibility-statement')} 
                      className="text-left text-foreground/70 hover:text-primary transition-colors"
                    >
                      Accessibility Statement
                    </button>
                  </li>
                  <li><a href="mailto:support@pantheraadvisors.com" className="text-foreground/70 hover:text-primary transition-colors">Support</a></li>
                </ul>
              </div>
              
              <div className="bg-background/50 rounded-lg p-6">
                <h3 className="font-semibold text-lg mb-4">Contact Information</h3>
                <ul className="space-y-3">
                  <li className="flex flex-col gap-1">
                    <span className="text-primary font-medium">Email:</span>
                    <a 
                      href="mailto:support@pantheraadvisors.com" 
                      className="text-foreground/70 hover:text-primary transition-colors break-words"
                    >
                      support@pantheraadvisors.com
                    </a>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">Phone:</span>
                    <a 
                      href="tel:+19174429401" 
                      className="text-foreground/70 hover:text-primary transition-colors"
                    >
                      +1 917-442-9401
                    </a>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">Address:</span>
                    <span className="text-foreground/70">
                      8 Walmsley Road, 06820 Darien CT
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <div className="border-t border-border mt-12 pt-8 text-center">
          <p className="text-foreground/60 text-sm">
            &copy; {currentYear} Panthera Advisors LLC. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
